import { IProductFamilyDetail, pimFileToMenuHrefProps, pimImageToOpenGraphImage } from '@viabizzuno/models';
import { getLayout } from '@viabizzuno/models/server';
import { LayoutDefault } from '@viabizzuno/ui';
import { getPageProps } from '@viabizzuno/ui/server';
import { asEquatable, IStaticContext, PageProps } from '@websolutespa/bom-core';
import { LabelService } from '@websolutespa/bom-mixer-hooks';
import { getPage, getStaticPathsForSchema } from '@websolutespa/bom-mixer-models/server';
import { LazyLoader, OpenGraph } from '@websolutespa/bom-mixer-ui';
import { BreadcrumbStructuredData } from '@viabizzuno/ui';
import { GetStaticPropsResult } from 'next';

export default function ProductFamilyDetail({ layout, page, params }: PageProps<IProductFamilyDetail>) {
  return (
    <>
      <LazyLoader components={page.components} />
      <BreadcrumbStructuredData />
      <OpenGraph item={(metaData) => ({
        ...metaData.openGraph,
        image: page.pimJson?.foto_preview_accesa ? pimImageToOpenGraphImage(page.pimJson.foto_preview_accesa) : undefined,
      })} />
    </>
  );
}

ProductFamilyDetail.Layout = LayoutDefault;

export async function getStaticProps(context: IStaticContext): Promise<GetStaticPropsResult<PageProps<IProductFamilyDetail>>> {
  const id = asEquatable(context.params.id);
  const market = context.params.market;
  const locale = context.params.locale;
  const layout = await getLayout(market, locale);
  const labelService = new LabelService(layout.labels);
  const page = await getPage<IProductFamilyDetail>('product_family_detail', id, market, locale, context);

  if (!page) {
    return {
      notFound: true,
      revalidate: true,
    };
  }

  const allCmsComponents = page.components || [];
  const cmsProjectsProposition = allCmsComponents.find(c => c.blockType === 'block_projects_proposition');
  const cmsComponents = allCmsComponents.filter(c => c.blockType !== 'block_projects_proposition');
  const components: typeof allCmsComponents = [];
  components.push({
    schema: 'block_hero_2cols',
    abstract: page.pimJson?.testo_di_prodotto,
    media: page.pimJson?.foto_quadrata,
    nav: pimFileToMenuHrefProps(page.pimJson?.manuale_della_luce, labelService.getLabel('product_family_detail.download_manual')),
    designers: page.designers,
  });
  if (page.pimJson?.immagine_full) {
    components.push({
      schema: 'block_banner_media',
      media: page.pimJson.immagine_full,
    });
  }
  components.push({
    schema: 'block_carousel_products',
    title: labelService.getLabel('product_family_detail.carousel_products.title'),
  });
  if (page.pimJson?.gallery) {
    components.push({
      schema: 'block_carousel_media',
      title: labelService.getLabel('product_family_detail.gallery.title'),
      ...(page.pimJson?.gallery ? { items: [page.pimJson?.gallery].flat().map((item) => ({ item: item })) } : { items: [] }),
    });
  }
  components.push({
    schema: 'block_designers_proposition',
  });
  components.push(...cmsComponents);
  components.push(cmsProjectsProposition ?? { schema: 'block_projects_proposition' });
  page.components = components;

  const props = await getPageProps({ ...context, layout, page });
  return {
    props,
    /*
    * Next.js will attempt to re-generate the page when a request comes in at most once every 60 seconds
    */
    revalidate: 60 * 60 * 2, // revalidate
  };
}

export async function getStaticPaths() {
  const paths = await getStaticPathsForSchema('product_family_detail');
  return {
    paths,
    fallback: 'blocking', // runs before initial render
    // fallback: true, // runs in the background
  };
}

